import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useParams } from 'react-router-dom';
import { connectToLocationSocket } from '../../api/location/LocationController';
import './LiveTrackingPage.css';
import 'leaflet/dist/leaflet.css';
import 'leaflet-defaulticon-compatibility';
import 'leaflet-defaulticon-compatibility/dist/leaflet-defaulticon-compatibility.css';

// ================= ICONS ================= //
const createRotatedMotoIcon = (rotation) => {
  return L.divIcon({
    className: 'moto-icon',
    html: `
      <img 
        src="/moto.png" 
        style="
          width: 64px;
          height: 64px;
          transform: rotate(${rotation}deg);
          transform-origin: center center;
          display: block;
        " 
      />
    `,
    iconSize: [64, 64],
    iconAnchor: [32, 42],
  });
};

const userIcon = L.icon({
  iconUrl: '/customer.png',
  iconSize: [40, 40],
  iconAnchor: [20, 40],
});

// ================= UTILS ================= //
const animateMarker = (marker, toLatLng, newIcon) => {
  const duration = 1000;
  const frames = 20;
  const interval = duration / frames;

  const fromLatLng = marker.getLatLng();
  const latDiff = (toLatLng.lat - fromLatLng.lat) / frames;
  const lngDiff = (toLatLng.lng - fromLatLng.lng) / frames;

  let frame = 0;

  const animate = () => {
    frame++;
    const nextLat = fromLatLng.lat + latDiff * frame;
    const nextLng = fromLatLng.lng + lngDiff * frame;
    marker.setLatLng(L.latLng(nextLat, nextLng));

    if (frame < frames) {
      setTimeout(animate, interval);
    } else {
      marker.setLatLng(toLatLng);
    }
  };

  marker.setIcon(newIcon);
  animate();
};

const calculateBearing = (from, to) => {
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const deltaLng = ((to.lng - from.lng) * Math.PI) / 180;

  const x = Math.sin(deltaLng) * Math.cos(lat2);
  const y = Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLng);

  let initialBearing = Math.atan2(x, y); // DÜZGÜN SIRALAMA: atan2(x, y)
  initialBearing = (initialBearing * 180) / Math.PI;

  const compassBearing = (initialBearing + 360) % 360;
  return compassBearing;
};

// ================= COMPONENTS ================= //
const RealTimeCourierMarker = ({ userId, setCourierLatLng }) => {
  const map = useMap();
  const markerRef = useRef(null);

  const isTooFar = (oldLatLng, newLatLng, threshold = 0.0005) => {
    const dLat = Math.abs(oldLatLng.lat - newLatLng.lat);
    const dLng = Math.abs(oldLatLng.lng - newLatLng.lng);
    return dLat > threshold || dLng > threshold;
  };

  const isVeryClose = (a, b, threshold = 0.00005) => {
    return a.distanceTo(b) < threshold * 111000; // ~5 metr
  };

  useEffect(() => {
    if (!userId) return;

    const unsubscribe = connectToLocationSocket(userId, (locationData) => {
      const lat = Number(parseFloat(locationData.latitude).toFixed(6));
      const lng = Number(parseFloat(locationData.longitude).toFixed(6));
      const newLatLng = L.latLng(lat, lng);

      setCourierLatLng(newLatLng);

      if (!markerRef.current) {
        const icon = createRotatedMotoIcon(0);
        markerRef.current = L.marker(newLatLng, { icon }).addTo(map);
      } else {
        const oldLatLng = L.latLng(
          Number(markerRef.current.getLatLng().lat.toFixed(6)),
          Number(markerRef.current.getLatLng().lng.toFixed(6))
        );

        if (isVeryClose(oldLatLng, newLatLng)) return;
        if (isTooFar(oldLatLng, newLatLng)) return;

        const bearing = calculateBearing(oldLatLng, newLatLng);
        const newIcon = createRotatedMotoIcon(bearing);
        animateMarker(markerRef.current, newLatLng, newIcon);
        map.panTo(newLatLng);
      }
    });

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [map, userId, setCourierLatLng]);

  return null;
};

const UserLocationMarker = ({ setUserLatLng }) => {
  const map = useMap();
  const markerRef = useRef(null);
  const firstCentered = useRef(false);

  useEffect(() => {
    if (!("geolocation" in navigator)) return;

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const newLatLng = L.latLng(lat, lng);

        setUserLatLng(newLatLng);

        if (markerRef.current) {
          markerRef.current.setLatLng(newLatLng);
        } else {
          markerRef.current = L.marker(newLatLng, { icon: userIcon }).addTo(map);
        }

        if (!firstCentered.current) {
          firstCentered.current = true;
          map.setView(newLatLng, 16);
        }
      },
      (err) => {
        console.error("İstifadəçi lokasiyası alınmadı:", err);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [map, setUserLatLng]);

  return null;
};

// ================= MAIN PAGE ================= //
const LiveTrackingPage = () => {
  const { trackingId } = useParams();
  const [courierData, setCourierData] = useState(null);
  const [userLatLng, setUserLatLng] = useState(null);
  const [courierLatLng, setCourierLatLng] = useState(null);
  const [hasFit, setHasFit] = useState(false);
  const mapRef = useRef();

  useEffect(() => {
    const fetchCourierData = async () => {
      try {
        const res = await fetch(`https://mototaksi.az:9898/api/v5/user/by-location-url?url=https://mototaksi.az/location/${trackingId}`);
        const data = await res.json();
        setCourierData(data);
      } catch (err) {
        console.error("Kuryer məlumatı alınmadı:", err);
      }
    };
    if (trackingId) fetchCourierData();
  }, [trackingId]);

  // Fit to both points ONCE
  useEffect(() => {
    if (!hasFit && userLatLng && courierLatLng && mapRef.current) {
      mapRef.current.fitBounds(L.latLngBounds([userLatLng, courierLatLng]), { padding: [50, 50] });
      setHasFit(true);
    }
  }, [userLatLng, courierLatLng, hasFit]);

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    let cleanedNumber = phone.replace(/\D/g, '');
    if (cleanedNumber.startsWith('0')) return '994' + cleanedNumber.slice(1);
    if (!cleanedNumber.startsWith('994')) return '994' + cleanedNumber;
    return cleanedNumber;
  };

  return (
    <div className="map-container">
      <MapContainer
        center={[40.4093, 49.8671]}
        zoom={16}
        scrollWheelZoom
        className="map"
        whenCreated={(mapInstance) => (mapRef.current = mapInstance)}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          attribution="Map data © OpenStreetMap contributors"
        />
        <UserLocationMarker setUserLatLng={setUserLatLng} />
        {courierData?.id && (
          <RealTimeCourierMarker userId={courierData.id} setCourierLatLng={setCourierLatLng} />
        )}

      </MapContainer>

      {courierData && (
        <div className="courier-card">
          <div className="courier-info">
            <img
              className="courier-photo"
              src={courierData.identifyPhoto?.[0] || "/favicon.ico"}
              alt="courier"
            />
            <div>
              <div className="courier-name">{courierData.name}</div>
              <a
                className="courier-phone"
                href={`https://wa.me/${formatPhoneNumber(courierData.phoneNumber)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                📞 {courierData.phoneNumber}
              </a>
            </div>
          </div>
          <img src="/moto.png" className="moto-icon-right" alt="moto" />
        </div>
      )}
    </div>
  );
};

export default LiveTrackingPage;

